import React, { useState, useEffect } from "react";
import { axiosInstance } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Loader2, Eye, EyeOff, ShieldCheck, KeyRound, CheckCircle2, XCircle, Download, FileText } from "lucide-react";

export default function OpenAIByoKeySetup() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [key, setKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const refreshStatus = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get("/jha-model-test/status");
      setStatus(r.data);
    } catch (e) {
      setStatus({ error: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshStatus();
  }, []);

  const submitKey = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);
    try {
      const r = await axiosInstance.post("/jha-model-test/set-key", {
        openai_key: key.trim(),
      });
      setResult({ ok: true, ...r.data });
      setKey(""); // wipe field after success
      await refreshStatus();
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  const verifyKey = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const r = await axiosInstance.post("/jha-model-test/verify");
      setVerifyResult(r.data);
    } catch (e) {
      setVerifyResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="openai-byo-key-setup-page">
      <div className="flex items-start gap-3">
        <KeyRound className="h-7 w-7 text-blue-600 mt-1" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            BYO OpenAI Key — GPT-6.1 Sol Test
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Isolated test harness. Production remains on <code>gpt-4o</code>.
          </p>
        </div>
      </div>

      {/* Test matrix reports */}
      <Card data-testid="test-reports-card" className="border-blue-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" /> LLM Evaluation Reports
          </CardTitle>
          <CardDescription className="text-xs">
            Download the generated PDFs from the 10-point test matrix.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <a
            href="/SafetyMind_AI_FULL_Test_Matrix.pdf"
            target="_blank"
            rel="noopener noreferrer"
            download
            data-testid="download-master-report-btn"
          >
            <Button className="w-full justify-start gap-2 bg-blue-600 hover:bg-blue-700 text-white">
              <Download className="h-4 w-4" />
              Master Report — Full Test Matrix (10 tests, 5 models, 9 pages)
            </Button>
          </a>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-1">
            <a href="/Tests_2_7_Report.pdf" target="_blank" rel="noopener noreferrer" download data-testid="download-t27-btn">
              <Button variant="outline" className="w-full justify-start gap-2">
                <Download className="h-4 w-4" />
                Tests 2 &amp; 7 — OSHA + Translation
              </Button>
            </a>
            <a href="/Tests_6_8_9_Report.pdf" target="_blank" rel="noopener noreferrer" download data-testid="download-t689-btn">
              <Button variant="outline" className="w-full justify-start gap-2">
                <Download className="h-4 w-4" />
                Tests 6, 8, 9 — Library / Routine / Adversarial
              </Button>
            </a>
            <a href="/Test3_Audit_Photo_BLIND.pdf" target="_blank" rel="noopener noreferrer" download data-testid="download-t3-btn">
              <Button variant="outline" className="w-full justify-start gap-2">
                <Download className="h-4 w-4" />
                Test 3 — Audit Photo (BLIND)
              </Button>
            </a>
          </div>
          <p className="text-xs text-slate-500 pt-2">
            Share the Master Report with your OpenAI advisor. It contains cost/latency heatmaps, per-test observations, hallucination flags, and the two-tier production rollout recommendation.
          </p>
        </CardContent>
      </Card>

      {/* Status card */}
      <Card data-testid="status-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-slate-600" /> Current Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading...
            </div>
          ) : status?.error ? (
            <div className="text-sm text-red-600">{status.error}</div>
          ) : (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="text-slate-500">Feature flag enabled</div>
              <div>
                {status.feature_flag_enabled ? (
                  <Badge className="bg-green-100 text-green-800">ENABLED</Badge>
                ) : (
                  <Badge className="bg-slate-200 text-slate-700">DORMANT</Badge>
                )}
              </div>

              <div className="text-slate-500">BYO key present</div>
              <div>
                {status.byo_key_present ? (
                  <Badge className="bg-green-100 text-green-800">
                    YES — ends in …{status.byo_key_last4}
                  </Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-800">NOT SET</Badge>
                )}
              </div>

              <div className="text-slate-500">Production model</div>
              <div className="font-mono text-xs text-slate-700">{status.production_model}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Paste key form */}
      <Card data-testid="key-form-card">
        <CardHeader>
          <CardTitle className="text-base">Paste your OpenAI API key</CardTitle>
          <CardDescription>
            The key is sent only to your SafetyMind backend over HTTPS, written to
            <code className="mx-1 bg-slate-100 px-1">/app/backend/.env</code>
            on the server, and never logged. Only the last 4 characters are returned.
            Current <code>.env</code> is backed up before the write.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submitKey} className="space-y-4">
            <div>
              <Label htmlFor="openai-key" className="text-sm">
                OpenAI API key (starts with <code>sk-proj-</code> or <code>sk-</code>)
              </Label>
              <div className="relative mt-1">
                <Input
                  id="openai-key"
                  data-testid="openai-key-input"
                  type={showKey ? "text" : "password"}
                  placeholder="sk-proj-..."
                  autoComplete="off"
                  spellCheck={false}
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  className="pr-10 font-mono text-sm"
                  required
                  minLength={20}
                />
                <button
                  type="button"
                  onClick={() => setShowKey((s) => !s)}
                  className="absolute inset-y-0 right-2 flex items-center text-slate-400 hover:text-slate-600"
                  aria-label={showKey ? "Hide key" : "Show key"}
                  data-testid="toggle-key-visibility"
                >
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Last 4 characters only will be shown back to you. Nothing else echoed.
              </p>
            </div>

            <Button
              type="submit"
              disabled={submitting || !key || !key.startsWith("sk-")}
              data-testid="submit-key-button"
              className="w-full"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Storing…
                </>
              ) : (
                <>Store key securely and enable test</>
              )}
            </Button>
          </form>

          {result && (
            <Alert
              className={`mt-4 ${
                result.ok ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"
              }`}
              data-testid="submit-result"
            >
              {result.ok ? (
                <CheckCircle2 className="h-4 w-4 text-green-700" />
              ) : (
                <XCircle className="h-4 w-4 text-red-700" />
              )}
              <AlertTitle>{result.ok ? "Key stored" : "Could not store key"}</AlertTitle>
              <AlertDescription className="text-xs">
                {result.ok
                  ? `Length ${result.key_length}, ends in …${result.key_last4}. Backup at ${result.backup_path}.`
                  : String(result.error)}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Verify */}
      <Card data-testid="verify-card">
        <CardHeader>
          <CardTitle className="text-base">Verify access to gpt-6.1-sol</CardTitle>
          <CardDescription>
            Sends one tiny ping to OpenAI (max 20 tokens) to confirm the key works
            and GPT-6.1 Sol is accessible on your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            onClick={verifyKey}
            disabled={verifying || !status?.byo_key_present}
            data-testid="verify-button"
            variant="outline"
          >
            {verifying ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Pinging gpt-6.1-sol…
              </>
            ) : (
              "Run verification ping"
            )}
          </Button>

          {verifyResult && (
            <Alert
              className={`${
                verifyResult.ok ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"
              }`}
              data-testid="verify-result"
            >
              {verifyResult.ok ? (
                <CheckCircle2 className="h-4 w-4 text-green-700" />
              ) : (
                <XCircle className="h-4 w-4 text-red-700" />
              )}
              <AlertTitle>{verifyResult.ok ? "Access confirmed" : "Verification failed"}</AlertTitle>
              <AlertDescription className="text-xs font-mono mt-2">
                <pre className="whitespace-pre-wrap break-words">
                  {JSON.stringify(verifyResult, null, 2)}
                </pre>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Alert className="border-slate-300 bg-slate-50">
        <ShieldCheck className="h-4 w-4" />
        <AlertTitle>What this page does and does not do</AlertTitle>
        <AlertDescription className="text-xs space-y-1 mt-2">
          <div>• Writes <code>OPENAI_API_KEY_BYO</code> and <code>ENABLE_BYO_GPT61_TEST=true</code> to <code>/app/backend/.env</code>.</div>
          <div>• Only this page and <code>/api/jha-model-test/*</code> endpoints can read the key.</div>
          <div>• Super-admin role required (worker/admin tokens get 403).</div>
          <div>• Production pre-construction JHA endpoint keeps using <code>gpt-4o</code> until you explicitly swap it.</div>
        </AlertDescription>
      </Alert>
    </div>
  );
}
